import type { HistoryEntry } from '../../domain/history';
import type { HistoryPort } from '../../ports/history';
import { createMemoryHistory } from './memory-history';

/**
 * Zgodovina nad IndexedDB (T2 §3).
 *
 * Object store `history` s ključem `id` in indeksoma `at` (za časovni red) in
 * `stationId` (za brisanje ene postaje). Vse, kar se dotika `indexedDB`, je
 * tukaj — store in domena porta ne poznata.
 *
 * ⚠️ **Ob nedosegljivi shrambi pade na pomnilnik.** V zasebnem načinu
 * Safarija in ob zavrnjeni kvoti `indexedDB.open()` vrže ali obvisi; aplikacija
 * mora teči tudi tam, le brez trajne zgodovine. Napake zato **ne potujejo
 * navzgor** — klicno mesto z njimi ne more storiti nič pametnejšega.
 */

const DB_NAME = 'nesywave';
const DB_VERSION = 1;
const STORE = 'history';

/**
 * Kotalno okno (T2 §3): obdrži zadnjih ~5.000 vnosov.
 *
 * localStorage bi pri neomejeni rasti počil; IndexedDB ima višjo kvoto, a
 * neskončno ni. Prag je udoben — nekaj mesecev gostega poslušanja — in
 * čiščenje teče ob dodajanju, ne ob branju.
 */
const MAX_ENTRIES = 5000;

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, DB_VERSION);
    open.onupgradeneeded = () => {
      const db = open.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('at', 'at');
        store.createIndex('stationId', 'stationId');
      }
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });
}

export function createIndexedDbHistory(): HistoryPort {
  // Ena sama odprta povezava za vso sejo; prvi klic jo vzpostavi. Ob napaki
  // ostane `null` in vsak klic pade na pomnilniško rezervo.
  let dbPromise: Promise<IDBDatabase> | null = null;
  const fallback = createMemoryHistory();
  let useFallback = false;

  function db(): Promise<IDBDatabase> {
    if (dbPromise === null) dbPromise = openDb();
    return dbPromise;
  }

  async function tx<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => Promise<T>,
  ): Promise<T> {
    if (useFallback || typeof indexedDB === 'undefined') {
      throw new Error('indexedDB ni na voljo');
    }
    const database = await db();
    const store = database.transaction(STORE, mode).objectStore(STORE);
    return run(store);
  }

  return {
    async recent(limit) {
      try {
        return await tx('readonly', (store) => collectRecent(store.index('at'), limit));
      } catch {
        useFallback = true;
        return fallback.recent(limit);
      }
    },

    async append(entry) {
      try {
        await tx('readwrite', async (store) => {
          await request(store.put(entry));
          const count = await request(store.count());
          if (count > MAX_ENTRIES) await prune(store, count - MAX_ENTRIES);
        });
      } catch {
        useFallback = true;
        await fallback.append(entry);
      }
    },

    async replaceAll(next) {
      try {
        await tx('readwrite', async (store) => {
          await request(store.clear());
          // Obdrži le kotalno okno; obnovljena kopija je lahko večja od kvote.
          const kept = [...next].sort((a, b) => b.at - a.at).slice(0, MAX_ENTRIES);
          for (const entry of kept) await request(store.put(entry));
        });
      } catch {
        useFallback = true;
        await fallback.replaceAll(next);
      }
    },

    async clearAll() {
      try {
        await tx('readwrite', (store) => request(store.clear()));
      } catch {
        useFallback = true;
        await fallback.clearAll();
      }
    },

    async clearStation(stationId) {
      try {
        await tx('readwrite', (store) => deleteByStation(store, stationId));
      } catch {
        useFallback = true;
        await fallback.clearStation(stationId);
      }
    },
  };
}

/** Zadnjih `limit` vnosov po indeksu `at`, od najnovejšega. */
function collectRecent(index: IDBIndex, limit: number): Promise<HistoryEntry[]> {
  return new Promise((resolve, reject) => {
    const entries: HistoryEntry[] = [];
    const cursorReq = index.openCursor(null, 'prev');
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor === null || entries.length >= limit) {
        resolve(entries);
        return;
      }
      entries.push(cursor.value as HistoryEntry);
      cursor.continue();
    };
    cursorReq.onerror = () => reject(cursorReq.error);
  });
}

/** Zbriši `count` najstarejših vnosov (kotalno okno). */
function prune(store: IDBObjectStore, count: number): Promise<void> {
  return new Promise((resolve, reject) => {
    let removed = 0;
    const cursorReq = store.index('at').openCursor(null, 'next');
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor === null || removed >= count) {
        resolve();
        return;
      }
      cursor.delete();
      removed += 1;
      cursor.continue();
    };
    cursorReq.onerror = () => reject(cursorReq.error);
  });
}

function deleteByStation(store: IDBObjectStore, stationId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cursorReq = store.index('stationId').openCursor(IDBKeyRange.only(stationId));
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor === null) {
        resolve();
        return;
      }
      cursor.delete();
      cursor.continue();
    };
    cursorReq.onerror = () => reject(cursorReq.error);
  });
}
